import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Mail, MapPin, Linkedin, ExternalLink, Shield } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import AuthorAvatar from '@/components/AuthorAvatar';
import SiteFooter from '@/components/SiteFooter';
import { AUTHOR, ORG, personLd } from '@/config/authorProfile';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* JSON-LD for AdSense reviewers + Google */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personLd()) }}
      />

      {/* Header */}
      <header className="container mx-auto px-4 sm:px-6 py-6 max-w-4xl">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity min-w-0">
            <AppLogo size={36} className="flex-shrink-0" />
            <div className="leading-tight min-w-0">
              <p className="heading text-sm font-semibold tracking-tight text-slate-900">
                How Busy Is My Airport
              </p>
            </div>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to predictor</span>
            <span className="sm:hidden">Back</span>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 pb-16 max-w-3xl">
        {/* Hero — real face, not stock */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-6 pb-10"
          data-testid="about-hero"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-4">
            About the site
          </p>
          <h1 className="heading text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-6">
            Why I built How Busy Is My Airport
          </h1>
          <div className="flex items-center gap-4 pb-6 border-b border-slate-200">
            <AuthorAvatar size={64} />
            <div className="leading-tight">
              <p className="font-semibold text-slate-900">{AUTHOR.name}</p>
              <p className="text-sm text-slate-500">{AUTHOR.role} · {AUTHOR.location}</p>
              <div className="flex items-center gap-3 mt-1">
                <a
                  href={`mailto:${AUTHOR.email}`}
                  className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  <Mail className="w-3 h-3" /> {AUTHOR.email}
                </a>
                {AUTHOR.linkedin && (
                  <a
                    href={AUTHOR.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    <Linkedin className="w-3 h-3" /> LinkedIn
                  </a>
                )}
              </div>
            </div>
          </div>
        </motion.section>

        {/* Full bio */}
        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 sm:p-8">
            <div className="prose prose-slate max-w-none text-[15px] leading-[1.75] text-slate-700 space-y-4">
              {AUTHOR.bio.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Credentials — small print, high trust */}
        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 sm:p-8">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-3">
              Background
            </h2>
            <ul className="space-y-2 text-sm text-slate-700">
              {AUTHOR.credentials.map((c, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* What we do / don't do */}
        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 sm:p-8">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-4">
              What this site does — and doesn't
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700 mb-2">
                  We do
                </p>
                <ul className="space-y-1.5 text-sm text-slate-700">
                  <li>• Predict crowd levels from live flight schedules</li>
                  <li>• Cover 331+ airports, updated hourly</li>
                  <li>• Publish editorial guides for major hubs</li>
                  <li>• Disclose every affiliate relationship</li>
                </ul>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-rose-700 mb-2">
                  We don't
                </p>
                <ul className="space-y-1.5 text-sm text-slate-700">
                  <li>• Sell or share your data</li>
                  <li>• Require signup for predictions</li>
                  <li>• Take pay-for-play from airlines or lounges</li>
                  <li>• Publish content we haven't personally reviewed</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data sources */}
        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 sm:p-8">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Where our data comes from
            </h2>
            <p className="text-sm text-slate-700 leading-relaxed mb-3">
              Every crowd score is derived from real, dated flight schedules —
              not survey data, not guesses. Our primary source is{' '}
              <a
                href="https://flightaware.com/commercial/aeroapi"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                FlightAware AeroAPI
              </a>
              , the same feed used by airlines and airports. We supplement it
              with FAA delay reports and airport-published terminal data. Read
              the full model on our{' '}
              <Link to="/methodology" className="text-blue-600 hover:underline">
                methodology page
              </Link>
              .
            </p>
          </CardContent>
        </Card>

        {/* Editorial independence */}
        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 sm:p-8">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-3">
              Editorial independence
            </h2>
            <p className="text-sm text-slate-700 leading-relaxed">
              This site accepts affiliate revenue from parking, credit-card,
              and lounge partners. We{' '}
              <span className="font-semibold text-slate-900">do not</span> accept
              paid placements, sponsored posts, or "review swaps." Editorial
              decisions — which airports to cover, which cards to recommend,
              which times to flag — are made independently of any commercial
              relationship. Read our{' '}
              <Link to="/editorial-standards" className="text-blue-600 hover:underline">
                editorial standards
              </Link>{' '}
              and{' '}
              <Link to="/affiliate-disclosure" className="text-blue-600 hover:underline">
                affiliate disclosure
              </Link>
              .
            </p>
          </CardContent>
        </Card>

        {/* Contact CTA */}
        <div className="text-center py-6" data-testid="about-contact-cta">
          <p className="text-slate-600 mb-3">Questions, corrections, or story ideas?</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Get in touch
            <ExternalLink className="w-3.5 h-3.5" />
          </Link>
          <p className="text-xs text-slate-500 mt-3 flex items-center justify-center gap-1">
            <MapPin className="w-3 h-3" />
            Operated by {ORG.legalOwner}, {ORG.mailingAddress.city}, {ORG.mailingAddress.region}
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
